import React, { useEffect, useState, useRef } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  AppBar,
  Badge,
  Box,
  Link,
  Hidden,
  IconButton,
  Toolbar,
  makeStyles,
  Select,
  MenuItem
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import NotificationsIcon from '@material-ui/icons/NotificationsOutlined';
import InputIcon from '@material-ui/icons/Input';
import IconLogo from 'src/shared/ui/components/IconLogo';
import { t } from 'src/shared/literals';
import { useSSE } from 'src/shared/support/hooks/useSSE';
import * as environments from 'src/environments.json';
import authorizeUser from 'src/auth/use-cases/authorizeUser';

const env = environments.default[process.env.REACT_APP_ENVIRONMENT.trim()];
const sseUrl = `${env.api.url}/sse`;

const useStyles = makeStyles((theme) => ({
  root: {},
  avatar: {
    width: 60,
    height: 60
  },
  toolbar: {
    minHeight: '48px'
  },
  selectItem: {
    color: 'white'
  },
  logoutIcon: {
    position: 'absolute',
    top: 0,
    right: -10,
    color: 'white'
  },
  badge: {
    '& .MuiBadge-badge': {
      backgroundColor: '#f44336', // Rojo error standard
      color: '#f44336',
      zIndex: 1000,
      border: `2px solid ${theme.palette.background.paper}`,
      height: 12,
      minWidth: 12,
      borderRadius: '50%'
    }
  }
}));

const getSelectedUserHeadquarter = () => {
  if (localStorage.selectedUserHeadquarter) {
    if (
      localStorage.selectedUserHeadquarter === 'all' &&
      localStorage.userHeadquarterList &&
      JSON.parse(localStorage.userHeadquarterList).length === 1
    ) {
      return JSON.parse(localStorage.userHeadquarterList)[0].CompanyHeadquarter
        .id;
    } else {
      return localStorage.selectedUserHeadquarter;
    }
  } else {
    return localStorage.userHeadquarterList
      ? JSON.parse(localStorage.userHeadquarterList)[0].CompanyHeadquarter.id
      : 0;
  }
};

const getUserHeadquarterList = () => {
  let userHeadquarterList =
    localStorage.userHeadquarterList &&
    JSON.parse(localStorage.userHeadquarterList);

  if (userHeadquarterList && userHeadquarterList.length > 1) {
    userHeadquarterList.unshift({
      CompanyHeadquarter: {
        id: 'all',
        name: `${userHeadquarterList[0].CompanyHeadquarter.Company.name} - ${t(
          'all'
        )}`
      }
    });
  }

  return userHeadquarterList || [];
};

const TopBar = ({ className, onMobileNavOpen, ...rest }) => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [token, setToken] = useState(localStorage.accessToken);
  const [userHeadquarter, setUserHeadquarter] = useState(
    getSelectedUserHeadquarter()
  );
  const audioRef = useRef(new Audio('/static/sounds/notification.mp3'));

  useEffect(() => {
    // Desbloquear audio tras la primera interacción del usuario
    const unlockAudio = () => {
      audioRef.current
        .play()
        .then(() => {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        })
        .catch(() => {});
      document.removeEventListener('click', unlockAudio);
    };
    document.addEventListener('click', unlockAudio);
    return () => document.removeEventListener('click', unlockAudio);
  }, []);

  const handleSSEAuthError = async () => {
    if (localStorage.____________________) {
      const { email, password } = JSON.parse(localStorage.____________________);
      const res = await authorizeUser(email, password);
      if (res.statusCode === 200) {
        setToken(localStorage.accessToken); // Esto reinicia el SSE con el nuevo token
      }
    }
  };

  const sseData = useSSE(sseUrl, token, handleSSEAuthError);

  const userHeadquarterList = getUserHeadquarterList();

  useEffect(() => {
    if (sseData && sseData.type === 'notification') {
      const event = new CustomEvent('sse-notification', {
        detail: sseData,
        cancelable: true
      });
      const shouldNotify = window.dispatchEvent(event);

      if (shouldNotify) {
        setNotifications((prev) => [...prev, sseData]);
        audioRef.current.play().catch((e) => {
          console.error('Error al reproducir sonido:', e);
        });
      }
    }
  }, [sseData]);

  useEffect(() => {
    if (location.pathname === '/app/conversations') {
      setNotifications([]);
    }
  }, [location.pathname]);

  const handleBellClick = () => {
    setNotifications([]);
    navigate('/app/conversations');
  };

  const selectUserHeadquarter = (event) => {
    const { value } = event.target;
    localStorage.selectedUserHeadquarter = value;
    setUserHeadquarter(value);
    document.location.reload();
  };

  return (
    <AppBar className={clsx(classes.root, className)} elevation={0} {...rest}>
      <Toolbar variant="regular" className={classes.toolbar}>
        <RouterLink to="/">
          <IconLogo style={{ width: '40px', height: '40px' }} />
        </RouterLink>
        <Box flexGrow={1} />
        <Hidden mdDown>
          <Select
            value={userHeadquarter}
            className={classes.selectItem}
            onChange={selectUserHeadquarter}
          >
            {userHeadquarterList.map((companyHeadquarter) => (
              <MenuItem
                key={companyHeadquarter.CompanyHeadquarter.id}
                value={companyHeadquarter.CompanyHeadquarter.id}
              >
                {companyHeadquarter.CompanyHeadquarter.name}
              </MenuItem>
            ))}
          </Select>
          <IconButton color="inherit" onClick={handleBellClick}>
            <Badge
              badgeContent={notifications.length}
              color="error"
              variant="dot"
              className={classes.badge}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton color="inherit">
            <Link
              component={RouterLink}
              to="/logout"
              className={classes.logoutIcon}
            >
              <InputIcon />
            </Link>
          </IconButton>
        </Hidden>
        <Hidden lgUp>
          <IconButton color="inherit" onClick={onMobileNavOpen}>
            <MenuIcon />
          </IconButton>
        </Hidden>
      </Toolbar>
    </AppBar>
  );
};

TopBar.propTypes = {
  className: PropTypes.string,
  onMobileNavOpen: PropTypes.func
};

export default TopBar;
